import { HttpClient, HttpParams } from "@angular/common/http"
import { inject, Injectable } from "@angular/core"
import { map } from "rxjs"
import { normalizeApiDate } from "../../shared/utils/api-date"
import { environment } from "../../../../environments/environment"
import { NO_ERROR_HANDLING } from "../../shared/constants/strings"
import { ISpot } from "../../spots/interfaces/spot-recommendation.interface"
import {
  IAnnouncement,
  IIssue,
  IIssueReportRequest,
  IIssueReportResponse,
  IIssueType,
} from "../interfaces/hub.interface"

type AnnouncementResponse = Omit<IAnnouncement, "publish_start" | "publish_end"> & {
  publish_start: string | number
  publish_end?: string | number
}

@Injectable({ providedIn: "root" })
export class HubService {
  private readonly http = inject(HttpClient)
  private readonly baseUrl = `${environment.api.baseUrl}/RMBTControlServer`
  private readonly options = { headers: { [NO_ERROR_HANDLING]: "true" } }

  getAnnouncements(limit = 50, search = "") {
    let params = new HttpParams().set("limit", limit)
    if (search.trim()) params = params.set("search", search.trim())
    return this.http
      .get<AnnouncementResponse[]>(`${this.baseUrl}/announcements`, {
        ...this.options,
        params,
      })
      .pipe(
        map((announcements) =>
          announcements.map((announcement) => ({
            ...announcement,
            categories: announcement.categories ?? [],
            publish_start: normalizeApiDate(announcement.publish_start),
            publish_end:
              announcement.publish_end == null
                ? undefined
                : normalizeApiDate(announcement.publish_end),
          })),
        ),
      )
  }

  getSpots() {
    return this.http.get<ISpot[]>(`${this.baseUrl}/spots`, this.options)
  }

  getIssueTypes() {
    return this.http.get<IIssueType[]>(`${this.baseUrl}/issue-types`, this.options)
  }

  getIssues(issueTypeId: number) {
    return this.http.get<IIssue[]>(
      `${this.baseUrl}/issue-types/${issueTypeId}/issues`,
      this.options,
    )
  }

  submitIssueReport(request: IIssueReportRequest) {
    return this.http.post<IIssueReportResponse>(
      `${this.baseUrl}/issue-reports`,
      request,
      this.options,
    )
  }
}
